/**
 * API服务
 */
import axios, { AxiosError } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 120秒超时
});

// 请求拦截器 - 自动添加Token
api.interceptors.request.use(
  (config) => {
    // 优先从 localStorage 读取 token（最可靠），然后尝试从 zustand store 读取
    let token: string | null = localStorage.getItem('token');
    
    // 如果 localStorage 中没有，尝试从 store 读取
    if (!token) {
      try {
        // 动态导入避免循环依赖
        const { useAuthStore } = require('../store/authStore');
        token = useAuthStore.getState().token;
      } catch (e) {
        // 忽略错误
      }
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log('API 请求:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      hasToken: !!token,
      tokenLength: token?.length,
      tokenPreview: token ? `${token.substring(0, 10)}...` : 'none',
    });
    return config;
  },
  (error) => {
    console.error('请求拦截器错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器 - 统一错误处理
api.interceptors.response.use(
  (response) => {
    console.log('API 响应成功:', {
      url: response.config.url,
      status: response.status,
      data: response.data,
    });
    return response;
  },
  (error: AxiosError) => {
    // Token过期处理（只在真正的 401 错误时处理，排除登录/注册接口）
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // 登录和注册接口的 401 错误不应该清除 token（因为这些是认证失败，不是 token 过期）
      if (!url.includes('/api/auth/login') && !url.includes('/api/auth/register')) {
        console.warn('Token 已过期或无效，清除认证信息', {
          url,
          pathname: window.location.pathname,
        });
        
        // 延迟清除，避免在登录刚成功后立即清除
        setTimeout(() => {
          // 清除 localStorage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // 清除 zustand store 状态（动态导入避免循环依赖）
          import('../store/authStore').then(({ useAuthStore }) => {
            useAuthStore.getState().clearAuth();
          });
          // 如果不在登录页，跳转到登录页
          if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
            console.warn('重定向到登录页');
            window.location.href = '/login';
          }
        }, 100);
      } else {
        console.log('登录/注册接口的 401 错误，不清除 token');
      }
    }

    // 402 需要付费订阅，跳转到定价页
    if (error.response?.status === 402) {
      if (window.location.pathname !== '/pricing') {
        console.warn('需要付费订阅，跳转到定价页');
        window.location.href = '/pricing';
      }
    }

    // 统一错误处理
    const errorMessage = (error.response?.data as any)?.detail || error.message || '请求失败';
    console.error('API请求错误:', {
      url: error.config?.url,
      status: error.response?.status,
      message: errorMessage,
    });

    return Promise.reject(error);
  }
);

export interface FileUploadResponse {
  success: boolean;
  message: string;
  file_content?: string;
  old_outline?: string;
}

export interface AnalysisRequest {
  file_content: string;
  analysis_type: 'overview' | 'requirements';
}

export interface ContentGenerationRequest {
  outline: { outline: any[] };
  project_overview: string;
}

// 文档相关API
export const documentApi = {
  // 上传文件
  uploadFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<FileUploadResponse>('/api/document/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000, // 10分钟，大文件解析需要较长时间
    });
  },

  // 第一步：识别标段
  analyzePackages: async (fileContent: string) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE_URL}/api/document/analyze-packages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ file_content: fileContent }),
    });
  },

  // 分析文档（SSE 流式），支持 overview / requirements 两种类型
  analyzeDocumentStream: async (data: { file_content: string; analysis_type: 'overview' | 'requirements' }) => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE_URL}/api/document/analyze-project-info`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        file_content: data.file_content,
        selected_package: null,
        document_type: 'bid',
      }),
    });
  },


  exportWord: (data: any) =>
    fetch(`${API_BASE_URL}/api/document/export-word`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }),
};

// 方案扩写相关API

export default api;
